import {useState, useEffect} from 'react';

export type MetricType = 'submitted' | 'delivered';
export type TimePeriodType = 'daily' | 'weekly' | 'monthly';

export interface AgentData {
    rank: number;
    name: string;
    initials: string;
    premium: number;
    deals: number;
}

/* ─── API response types ─── */

export interface LeaderboardAgentEntry {
    agentName: string;
    deals: number;
    premium: number;
}

export interface LeaderboardResponse {
    data: Record<string, Record<string, LeaderboardAgentEntry[]>>;
    updatedAt: number;
}

/* ─── Key maps: UI state -> API keys ─── */

export const TIME_PERIOD_KEYS: Record<TimePeriodType, string> = {
    daily: 'Today',
    weekly: 'WTD',
    monthly: 'MTD',
} as const;

export const METRIC_KEYS: Record<MetricType, string> = {
    submitted: 'New Deal',
    delivered: 'Delivered',
} as const;

/* ─── Helpers ─── */

export function toAgentData(entry: LeaderboardAgentEntry, rank: number): AgentData {
    const parts = entry.agentName.trim().split(/\s+/);
    const first = parts[0] ?? '';
    const last = parts[parts.length - 1] ?? '';
    const initials = `${first[0] ?? ''}${parts.length > 1 ? (last[0] ?? '') : ''}`.toUpperCase();

    return {
        rank,
        name: entry.agentName,
        initials,
        premium: entry.premium,
        deals: entry.deals,
    };
}

export function formatRelativeTime(timestamp: number): string {
    const diffMs = Date.now() - timestamp;
    const diffMin = Math.floor(diffMs / 60_000);

    if (diffMin < 1) return 'Updated just now';
    if (diffMin < 60) return `Updated ${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `Updated ${diffHr}h ago`;
    return `Updated ${Math.floor(diffHr / 24)}d ago`;
}

/**
 * Re-evaluates `formatRelativeTime` on a tick so the "Updated Xm ago" label
 * stays current without waiting for a data refetch.
 */
export function useRelativeTime(timestamp: number | undefined, intervalMs = 30_000): string | null {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (timestamp == null) return;
        const id = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(id);
    }, [timestamp, intervalMs]);
    return timestamp != null ? formatRelativeTime(timestamp) : null;
}

export type DealSaleEvent = {
    properties: {
        agentName: string;
        amount: number;
        stage: string;
        stageLabel: string;
        dealId: number;
        occurredAt: number;
    };
};

export type DealSaleToast = {
    id: string;
    properties: {
        agentName: string;
        amount: number;
        stage: string;
        stageLabel: string;
        dealId: number;
        occurredAt: number;
    };
};

export const TOP_AGENTS_DELIVERED: AgentData[] = [
    {rank: 1, name: 'Marcus Chen', initials: 'MC', premium: 247850, deals: 0},
    {rank: 2, name: 'Priya Sharma', initials: 'PS', premium: 231420, deals: 0},
    {rank: 3, name: 'David Okafor', initials: 'DO', premium: 198760, deals: 0},
    {rank: 4, name: 'Sarah Mitchell', initials: 'SM', premium: 187340, deals: 0},
    {rank: 5, name: 'James Rivera', initials: 'JR', premium: 176890, deals: 0},
    {rank: 6, name: 'Aisha Johnson', initials: 'AJ', premium: 165230, deals: 0},
    {rank: 7, name: 'Tyler Nguyen', initials: 'TN', premium: 154870, deals: 0},
    {rank: 8, name: 'Rachel Kim', initials: 'RK', premium: 148920, deals: 0},
    {rank: 9, name: 'Omar Hassan', initials: 'OH', premium: 137650, deals: 0},
    {rank: 10, name: 'Emily Torres', initials: 'ET', premium: 129430, deals: 0},
];

export const TOP_AGENTS_SUBMITTED: AgentData[] = [
    {rank: 1, name: 'Priya Sharma', initials: 'PS', premium: 312500, deals: 0},
    {rank: 2, name: 'Marcus Chen', initials: 'MC', premium: 298100, deals: 0},
    {rank: 3, name: 'Sarah Mitchell', initials: 'SM', premium: 276340, deals: 0},
    {rank: 4, name: 'James Rivera', initials: 'JR', premium: 254890, deals: 0},
    {rank: 5, name: 'David Okafor', initials: 'DO', premium: 243760, deals: 0},
    {rank: 6, name: 'Tyler Nguyen', initials: 'TN', premium: 232100, deals: 0},
    {rank: 7, name: 'Aisha Johnson', initials: 'AJ', premium: 218430, deals: 0},
    {rank: 8, name: 'Emily Torres', initials: 'ET', premium: 205670, deals: 0},
    {rank: 9, name: 'Rachel Kim', initials: 'RK', premium: 194320, deals: 0},
    {rank: 10, name: 'Omar Hassan', initials: 'OH', premium: 183950, deals: 0},
];

export const CURRENT_AGENT: AgentData = {
    rank: 14,
    name: 'You',
    initials: 'YO',
    premium: 98450,
    deals: 0,
};

export const CURRENT_AGENT_SUBMITTED: AgentData = {
    rank: 16,
    name: 'You',
    initials: 'YO',
    premium: 142300,
    deals: 0,
};

export function formatPremium(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}
